/**
 * O registro da conferência no portal (E172.F7.T03). O estado decide o que se pede:
 * - a consulta tentada (consistente, divergente, indisponível) diz o endereço e o instante, e
 *   aceita o print;
 * - a que a página respondeu confronta campos;
 * - a que não respondeu, a não executada e a não aplicável dizem o motivo.
 * "Não aplicável" só aparece quando o material não traz registro. O instante sai no fuso deste
 * computador, e o servidor confere o resto.
 */
import { useState, type FormEvent } from 'react';

import { Button, Field, Input, Radio, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  instanteComFuso,
  type AlvoDaConferenciaData,
  type ConferenciaNoPortalInData,
  type EstadoDaConferencia,
} from '@/lib/conferenciaNoPortal';

import {
  CamposConfrontados,
  LINHA_VAZIA,
  camposDaTela,
  type LinhaDoCampo,
} from './CamposConfrontados';

const t = ptBR.conferenciaNoPortal;
const tf = t.formulario;
const EXECUTADAS: EstadoDaConferencia[] = ['consistente', 'divergente', 'indisponivel'];
const COM_CONFRONTO: EstadoDaConferencia[] = ['consistente', 'divergente'];

function estadosDoAlvo(alvo: AlvoDaConferenciaData): EstadoDaConferencia[] {
  const base: EstadoDaConferencia[] = [...EXECUTADAS, 'nao_executada'];
  return alvo.situacao === 'sem_registro' ? [...base, 'nao_aplicavel'] : base;
}

function comFuso(local: string): string | null {
  if (local === '') return null;
  return instanteComFuso(local, new Date(local).getTimezoneOffset());
}

interface Rascunho {
  url: string;
  codigo: string;
  consultadoEm: string;
  linhas: LinhaDoCampo[];
  motivo: string;
  print: File | null;
}

interface CamposDoEstadoProps {
  estado: EstadoDaConferencia;
  rascunho: Rascunho;
  onMudar: (parte: Partial<Rascunho>) => void;
}

/** O que cada estado pede: o endereço e o instante, os campos, o motivo e o print. */
function CamposDoEstado({ estado, rascunho, onMudar }: CamposDoEstadoProps): JSX.Element {
  const executada = EXECUTADAS.includes(estado);
  const comConfronto = COM_CONFRONTO.includes(estado);
  return (
    <>
      {estado === 'nao_aplicavel' ? null : (
        <div className="grid grid-cols-1 gap-sc-2 sm:grid-cols-2">
          <Field label={tf.endereco} required={executada}>
            <Input
              value={rascunho.url}
              maxLength={500}
              data-campo="url"
              onChange={(e) => onMudar({ url: e.target.value })}
            />
          </Field>
          <Field label={tf.codigo}>
            <Input
              value={rascunho.codigo}
              maxLength={255}
              data-campo="codigo"
              onChange={(e) => onMudar({ codigo: e.target.value })}
            />
          </Field>
          <Field label={tf.consultadoEm} description={tf.fuso} required={executada}>
            <Input
              type="datetime-local"
              value={rascunho.consultadoEm}
              data-campo="consultado_em"
              onChange={(e) => onMudar({ consultadoEm: e.target.value })}
            />
          </Field>
        </div>
      )}
      {comConfronto ? (
        <CamposConfrontados linhas={rascunho.linhas} onMudar={(linhas) => onMudar({ linhas })} />
      ) : (
        <Field label={tf.motivo} required>
          <Textarea
            value={rascunho.motivo}
            maxLength={500}
            rows={2}
            data-campo="motivo"
            onChange={(e) => onMudar({ motivo: e.target.value })}
          />
        </Field>
      )}
      {executada ? (
        <Field label={tf.print}>
          <Input
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            data-campo="print"
            onChange={(e) => onMudar({ print: e.target.files?.[0] ?? null })}
          />
        </Field>
      ) : null}
    </>
  );
}

/** A entrada do servidor pelo rascunho: só o que o estado usa. */
function entradaDoRascunho(
  estado: EstadoDaConferencia,
  r: Rascunho,
): { entrada: ConferenciaNoPortalInData; print: File | null } {
  const comConfronto = COM_CONFRONTO.includes(estado);
  return {
    entrada: {
      estado,
      url: r.url.trim() || null,
      codigo: r.codigo.trim() || null,
      consultado_em: comFuso(r.consultadoEm),
      campos: comConfronto ? camposDaTela(r.linhas) : [],
      motivo: comConfronto ? null : r.motivo.trim() || null,
    },
    print: EXECUTADAS.includes(estado) ? r.print : null,
  };
}

export interface FormularioDaConferenciaProps {
  alvo: AlvoDaConferenciaData;
  enviando: boolean;
  onRegistrar: (entrada: ConferenciaNoPortalInData, print: File | null) => Promise<boolean>;
}

export function FormularioDaConferencia({
  alvo,
  enviando,
  onRegistrar,
}: FormularioDaConferenciaProps): JSX.Element {
  const [estado, setEstado] = useState<EstadoDaConferencia | null>(null);
  const [rascunho, setRascunho] = useState<Rascunho>({
    url: alvo.url ?? '',
    codigo: '',
    consultadoEm: '',
    linhas: [LINHA_VAZIA],
    motivo: '',
    print: null,
  });
  const mudar = (parte: Partial<Rascunho>): void => setRascunho((r) => ({ ...r, ...parte }));

  async function registrar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    if (estado === null) return;
    const { entrada, print } = entradaDoRascunho(estado, rascunho);
    if (await onRegistrar(entrada, print)) {
      setEstado(null);
      mudar({ linhas: [LINHA_VAZIA], motivo: '', print: null });
    }
  }

  return (
    <form className="space-y-sc-3" onSubmit={registrar} data-testid="portal-formulario" noValidate>
      <h4 className="text-body text-fg">{tf.titulo}</h4>
      <fieldset className="space-y-sc-1">
        <legend className="text-caption text-fg-dim">{tf.estado}</legend>
        {estadosDoAlvo(alvo).map((e) => (
          <Radio
            key={e}
            name="portal-estado"
            value={e}
            checked={estado === e}
            onChange={() => setEstado(e)}
            data-estado={e}
          >
            {t.estadoNoFormulario[e]}
          </Radio>
        ))}
      </fieldset>
      {estado === null ? null : (
        <CamposDoEstado estado={estado} rascunho={rascunho} onMudar={mudar} />
      )}
      <Button type="submit" disabled={estado === null || enviando} data-testid="portal-registrar">
        {enviando ? tf.registrando : tf.registrar}
      </Button>
    </form>
  );
}
